/**
 * The space layer (RFC 0281 S4): which space this window is in, and keeping its
 * tree written down.
 *
 * `useLayout` owns `?v=`, the content tree. This owns `?space=`, the name that
 * tree belongs to, and the traffic between the url and the spaces document:
 *
 *   on load    resolve which space (§8's four steps), then put its tree in the url
 *   on change  write the tree back, debounced, trailing edge
 *
 * THE URL WINS. If the url already carries a `v` that differs from what the
 * space stores, the space is NOT overwritten until the next deliberate change.
 * Without that rule, opening a link somebody sent you silently rewrites your own
 * space with their layout — you would lose an arrangement by looking at theirs.
 *
 * DEBOUNCED, AND NEVER PER POINTER MOVE. A resize fires continuously while a
 * seam is dragged; the url absorbs that already (it writes with `replace: true`
 * for exactly this reason) and the document takes only the value the drag
 * settles on. Writing every frame would put a thousand changes into an Automerge
 * document whose history is permanent.
 */

import type { Repo } from '@automerge/automerge-repo'
import {
  createSpacesDoc,
  deleteSpace,
  putSpace,
  renameSpace,
  reorderSpace,
  setSpaceContent,
  setSpaceLayout,
  setSpacesDoc,
} from '@mod/core/model/spaces'
import { useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CONTENT_PARAM } from '../window/contentUrl'
import {
  NEW_SPACE,
  readLastSpace,
  resolveSpace,
  SPACE_PARAM,
  writeLastSpace,
} from './spaceUrl'
import { useSpaces } from './useSpaces'

/** How long a rearrangement sits before it reaches the document. */
const SAVE_DEBOUNCE_MS = 750

/** The name a World's first space gets. */
const FIRST_SPACE_NAME = 'Main'

/** What a NEW space opens on: one empty pane, a bare `n`.
 *
 *  The session list was the wrong answer here for the same reason it was the
 *  wrong thing to put in ⌘D's new half: it answers "what goes in this space"
 *  with sessions only, when the pane picker answers it with everything you have
 *  been in. A fresh space is a fresh surface, and the picker is what makes it
 *  one. */
const NEW_SPACE_CONTENT = 'n'

const mintSpaceId = () =>
  `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

export interface SpaceLayer {
  /** The space this window is in, or null while that is still being decided. */
  spaceId: string | null
  /** Every space in the World, for a switcher to render. */
  spaces: ReturnType<typeof useSpaces>['spaces']
  /** Go to a space: its tree replaces what is on screen, in one navigation. */
  switchTo: (spaceId: string) => void
  /**
   * Make a space and go to it.
   *
   * Without this the switcher, `⌘1..9` and the tabs are all unreachable: one
   * space is all a World would ever have, so every way of moving between them
   * has nothing to move to.
   */
  createSpace: (
    name?: string,
    /** What the new space opens on (RFC 0284 S6h). A layout's default tree and
     *  the layout that supplied it, so a person who arranged one gets it
     *  rather than the picker. Omitted keeps the picker, which is what ⌘T did
     *  before layouts carried panes. */
    seed?: { content?: string; layoutId?: string },
  ) => string | undefined
  /** Rename a space. Its tree and its place in the strip are untouched. */
  renameSpace: (spaceId: string, name: string) => void
  /**
   * Arrange a space with a layout, or put it back to the built-in (RFC 0284
   * S4b).
   *
   * `null` clears, which is the only way back once a person has chosen one,
   * and matters more than it sounds: a layout that empties the sidebar leaves
   * no sidebar to change the layout FROM.
   */
  setLayout: (spaceId: string, layoutId: string | null) => void
  /**
   * Move a space to a position in the strip.
   *
   * Which also rebinds `ctrl+1..9`, because `order` is both things, and it
   * syncs, so it rebinds them everywhere. Moving a space never switches to it:
   * dragging is arranging, not going.
   */
  reorderSpace: (spaceId: string, toIndex: number) => void
  /**
   * Delete a space.
   *
   * Destroys nothing (§7's invariant): every session and document it named is
   * still in its project, and the tree it held was a view. Leaving the space you
   * are IN moves you to another rather than to nowhere.
   */
  deleteSpace: (spaceId: string) => void
  /**
   * Write a tree into a space WITHOUT going there.
   *
   * For putting something in front of someone where it belongs, when where it
   * belongs is not what they are looking at. Switching would be the other way
   * to do it, and it is the wrong one twice over: it takes the person out of
   * whatever they were reading, and it abandons the outgoing space's tree,
   * which the save effect below has only debounced and not yet written.
   *
   * Refuses the space the window is IN, because there the url is the live tree
   * and this would be overwritten by the next flush. False when it did not
   * write, so a caller can say so rather than assume.
   */
  writeSpaceContent: (spaceId: string, content: string) => boolean
  /** Whether the World's spaces have been read at all (§8). */
  loaded: boolean
}

export function useSpaceLayer(repo: Repo): SpaceLayer {
  const [searchParams, setSearchParams] = useSearchParams()
  const { spaces, loaded, spacesDocId, worldId } = useSpaces()

  const urlSpaceId = searchParams.get(SPACE_PARAM)
  const content = searchParams.get(CONTENT_PARAM)

  const resolution = resolveSpace({
    urlSpaceId,
    lastOpenedId: readLastSpace(worldId),
    spaces,
    loaded,
  })
  const spaceId = resolution.kind === 'space' ? resolution.spaceId : null
  /**
   * The url names a space, and it is not the one we are in: its tree is that
   * other space's. A url with a tree and no space is a link, and stays ours.
   */
  const foreign =
    urlSpaceId !== null && spaceId !== null && urlSpaceId !== spaceId

  /**
   * Guards against doing the one-time things twice.
   *
   * React runs effects again on every dependency change, and both of these
   * WRITE: creating a second spaces document for a World would strand the
   * first, and re-seeding the url would fight whatever the person just did.
   */
  const creating = useRef(false)
  const seeded = useRef<string | null>(null)
  /** `?space=new` is answered once per window, however often this renders. */
  const madeOwn = useRef(false)

  /**
   * Spaces in the order they were last looked at, most recent last.
   *
   * What closing a space falls back to, the way Chrome returns to the tab you
   * came from rather than the first one. Kept in memory for this window only:
   * a reload starts the history over, and the stored last space covers where
   * a reload lands.
   */
  const visited = useRef<string[]>([])
  useEffect(() => {
    if (!spaceId) return
    visited.current = [
      ...visited.current.filter((id) => id !== spaceId),
      spaceId,
    ]
  }, [spaceId])

  /**
   * A World with no spaces gets its first one, holding what is on screen.
   *
   * Only on `create`, which `resolveSpace` returns exclusively for a spaces
   * document that has been READ and is genuinely empty. Seeded with the current
   * tree rather than with nothing, so the arrangement you already had becomes
   * the space you already had rather than being thrown away on the way in.
   */
  useEffect(() => {
    if (resolution.kind !== 'create' || creating.current || !worldId) return
    creating.current = true
    void (async () => {
      try {
        let docId = spacesDocId
        if (!docId) {
          docId = (await createSpacesDoc(repo)).documentId as string
          await setSpacesDoc(repo, worldId, docId)
        }
        const id = mintSpaceId()
        // Claimed BEFORE the write lands: the snapshot carrying this space can
        // render while `?space=new` is still in the url, and would make another.
        const answersNew = urlSpaceId === NEW_SPACE
        if (answersNew) madeOwn.current = true
        await putSpace(repo, docId, id, {
          name: FIRST_SPACE_NAME,
          content: content ?? '',
        })
        writeLastSpace(worldId, id)
        // A New Window into an empty World: the first space IS its own, so
        // `?space=new` is answered here and must not make a second one.
        if (answersNew) {
          seeded.current = id
          setSearchParams(
            (params) => {
              params.set(SPACE_PARAM, id)
              return params
            },
            { replace: true },
          )
        }
      } finally {
        creating.current = false
      }
    })()
  }, [
    resolution.kind,
    worldId,
    spacesDocId,
    repo,
    content,
    urlSpaceId,
    setSearchParams,
  ])

  /**
   * A window opened as New Window gets a space of its own (RFC 0343 3c).
   *
   * Empty-named, so it is labelled by what it holds, and opened on the pane
   * picker like ⌘T. The url is REPLACED, not pushed: Back must not return to
   * `?space=new` and make another.
   */
  useEffect(() => {
    if (resolution.kind !== 'new' || madeOwn.current || !spacesDocId) return
    madeOwn.current = true
    const id = mintSpaceId()
    void (async () => {
      await putSpace(repo, spacesDocId, id, {
        name: '',
        content: NEW_SPACE_CONTENT,
      })
      seeded.current = id
      writeLastSpace(worldId, id)
      setSearchParams(
        (params) => {
          params.set(SPACE_PARAM, id)
          params.set(CONTENT_PARAM, NEW_SPACE_CONTENT)
          return params
        },
        { replace: true },
      )
    })()
  }, [resolution.kind, spacesDocId, repo, worldId, setSearchParams])

  /**
   * Put the space's tree in the url, ONCE per space.
   *
   * `seeded` is keyed by space id so switching seeds the new one, and so a
   * rearrangement inside a space is never overwritten by the value the space
   * had when it was opened.
   *
   * THE URL WINS on arrival: a url that already names a tree is a link someone
   * sent, or a reload of work in progress, and either way it is more current
   * than the document. The space is left alone until the next change writes to
   * it.
   *
   * EXCEPT A TREE THAT NAMES ANOTHER SPACE. When the url's space is gone and
   * resolution fell back to this one, the tree beside it is the gone space's.
   * Closing a space deletes it from the document before the navigation away
   * lands, so for a render the url is exactly that: adopting it put the closed
   * space's panes into the one you went back to, and the save made it stick.
   */
  useEffect(() => {
    if (!spaceId) return
    if (seeded.current === spaceId && !foreign) return
    seeded.current = spaceId
    writeLastSpace(worldId, spaceId)
    if (content && !foreign) return
    const entry = spaces.find((s) => s.spaceId === spaceId)
    if (!entry?.content && !foreign) return
    setSearchParams(
      (params) => {
        params.set(SPACE_PARAM, spaceId)
        if (entry?.content) params.set(CONTENT_PARAM, entry.content)
        else params.delete(CONTENT_PARAM)
        return params
      },
      { replace: true },
    )
  }, [spaceId, spaces, content, foreign, setSearchParams, worldId])

  /**
   * Write the tree back, trailing edge.
   *
   * Skipped until the space has been seeded, or the first render of a switch
   * would save the OUTGOING tree over the incoming space. Skipped too while
   * the url's tree belongs to another space (see `foreign`).
   */
  useEffect(() => {
    if (!spaceId || !spacesDocId || seeded.current !== spaceId || foreign)
      return
    const tree = content ?? ''
    const timer = setTimeout(() => {
      void setSpaceContent(repo, spacesDocId, spaceId, tree)
    }, SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [spaceId, spacesDocId, content, foreign, repo])

  const switchTo = useCallback(
    (next: string) => {
      const entry = spaces.find((s) => s.spaceId === next)
      if (!entry) return
      // BOTH PARAMS IN ONE NAVIGATION, so a switch is one history entry and the
      // tree never renders against the wrong space id for a frame.
      seeded.current = next
      writeLastSpace(worldId, next)
      setSearchParams((params) => {
        params.set(SPACE_PARAM, next)
        if (entry.content) params.set(CONTENT_PARAM, entry.content)
        else params.delete(CONTENT_PARAM)
        return params
      })
    },
    [spaces, setSearchParams, worldId],
  )

  const writeSpaceContent = useCallback(
    (target: string, next: string) => {
      if (!spacesDocId || !next) return false
      // The space on screen is the url's, not the document's. Writing under it
      // would be undone by the next debounced flush, and reported as done.
      if (target === spaceId) return false
      if (!spaces.some((s) => s.spaceId === target)) return false
      void setSpaceContent(repo, spacesDocId, target, next)
      return true
    },
    [repo, spacesDocId, spaces, spaceId],
  )

  const createSpace = useCallback(
    (named?: string, seed?: { content?: string; layoutId?: string }) => {
      if (!spacesDocId) return
      const id = mintSpaceId()
      // UNNAMED unless the caller knows what the space is FOR, which is the
      // difference between ⌘T and an agent opening a space for a piece of work.
      // An empty name is labelled by what the space holds (`spaceLabel`), so it
      // reads as its work without a rename.
      const name = named?.trim() ?? ''
      // THE LAYOUT'S PANES, when it has any (RFC 0284 S6h). The picker stays
      // the answer for a layout that says nothing, which is every layout
      // written before this and every space nobody arranged.
      const content = seed?.content || NEW_SPACE_CONTENT
      void (async () => {
        await putSpace(repo, spacesDocId, id, {
          name,
          content,
          ...(seed?.layoutId ? { layoutId: seed.layoutId } : {}),
        })
        // SEEDED BEFORE NAVIGATING, so the save effect does not fire against the
        // outgoing tree while the new space is still arriving from the document.
        seeded.current = id
        writeLastSpace(worldId, id)
        setSearchParams((params) => {
          params.set(SPACE_PARAM, id)
          params.set(CONTENT_PARAM, content)
          return params
        })
      })()
      return id
    },
    [repo, spacesDocId, setSearchParams, worldId],
  )

  const rename = useCallback(
    (id: string, name: string) => {
      if (!spacesDocId || !name.trim()) return
      void renameSpace(repo, spacesDocId, id, name.trim())
    },
    [repo, spacesDocId],
  )

  /**
   * Arrange a space with a layout, or put it back to the built-in (RFC 0284
   * S4b).
   *
   * `null` clears, which is the only way back once a person has chosen one, and
   * matters more than it sounds: a layout that empties the sidebar leaves no
   * sidebar to change the layout FROM.
   */
  const setLayout = useCallback(
    (id: string, layoutId: string | null) => {
      if (!spacesDocId) return
      void setSpaceLayout(repo, spacesDocId, id, layoutId)
    },
    [repo, spacesDocId],
  )

  const reorder = useCallback(
    (id: string, toIndex: number) => {
      if (!spacesDocId) return
      // No `switchTo`, deliberately. Dragging a space is arranging the strip,
      // and jumping into whatever you just moved would throw away the tree you
      // were looking at while you tidied.
      void reorderSpace(repo, spacesDocId, id, toIndex)
    },
    [repo, spacesDocId],
  )

  const remove = useCallback(
    (id: string) => {
      if (!spacesDocId) return
      // The LAST space is not deletable: `resolveSpace` would answer `create`
      // and mint a fresh one, so the delete would look like a rename to
      // "Main" and lose the tree on the way.
      if (spaces.length <= 1) return
      // Move first, if this is where we are. Deleting the space you are in
      // would leave `spaceId` naming an entry that no longer exists, and the
      // next resolve would land you somewhere by accident rather than by
      // choice.
      // Back to the space you were in before this one, when it still exists;
      // the first other space only when there is no such history.
      if (id === spaceId) {
        const previous = [...visited.current]
          .reverse()
          .find((v) => v !== id && spaces.some((sp) => sp.spaceId === v))
        const next = previous ?? spaces.find((sp) => sp.spaceId !== id)?.spaceId
        if (next) switchTo(next)
      }
      visited.current = visited.current.filter((v) => v !== id)
      void deleteSpace(repo, spacesDocId, id)
    },
    [repo, spacesDocId, spaces, spaceId, switchTo],
  )

  return {
    spaceId,
    spaces,
    switchTo,
    createSpace,
    renameSpace: rename,
    setLayout,
    reorderSpace: reorder,
    deleteSpace: remove,
    writeSpaceContent,
    loaded,
  }
}
